<template>
  <div>
    <div v-if="showPureCheckbox">
      <v-checkbox
        v-for="(opt, index) in options"
        :key="opt.id"
        v-model="fieldValue"
        :label="opt.label"
        :value="opt.input_value"
        :value-comparator="checkInputChecked"
        :hide-details="index < options.length - 1"
        :error="!!errorMessages"
        :error-messages="errorMessages"
        color="orange50"
      >
        <template #label>
          <span class="wd-text-md wd-font-normal wd-text-black50">{{ opt.label }}</span>
        </template>
        <template #message>
          <span class="wd-text-red50">
            {{ errorMessages }}
          </span>
        </template>
      </v-checkbox>
    </div>
    <template v-else>
      <div @click="open()">
        <v-select
          :model-value="fieldValueIds"
          :items="options"
          item-title="value"
          item-value="id"
          color="orange50"
          multiple
          variant="outlined"
          readonly
          :placeholder="placeholder"
          :error-messages="errorMessages"
        >
          <template #selection="{ index }">
            <div v-if="index === 0" class="wd-flex wd-flex-nowrap wd-w-full">
              <div class="wd-max-w-90% wd-truncate">
                <span>{{ textValueLabel }}</span>
              </div>
              <div v-if="fieldValue.length >= 3" class="wd-ml-1 wd-mr-2 wd-min-w-max">( {{ fieldValue.length }} )</div>
            </div>
          </template>
          <template #append-inner>
            <div>
              <FunnowIcon class="mt-1" max-width="20" image-path="chevron-down.svg" />
            </div>
          </template>
        </v-select>
      </div>
    </template>
    <modal-pure-hint v-model="isOpen" :title="title" show-button @confirm="confirm" @close="close">
      <div>
        <div v-for="opt in options" :key="opt.id" class="not-first:wd-pt-3">
          <div class="wd-pl-3 wd-pr-5 wd-pb-4">
            <v-checkbox
              v-model="selectedItems"
              :label="opt.label"
              :value="opt.input_value"
              :value-comparator="checkInputChecked"
              hide-details
              color="orange50"
            >
              <template #label>
                <div class="wd-text-md wd-font-normal wd-text-black50">{{ opt.label }}</div>
              </template>
            </v-checkbox>
          </div>
          <v-divider />
        </div>
      </div>
    </modal-pure-hint>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useVModel } from '@vueuse/core'
import ModalPureHint from '@/components/ModalPureHint.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import type { FormFieldOptionCreationData } from '@/view-models/remark'

export default defineComponent({
  components: {
    ModalPureHint,
    FunnowIcon,
  },
  props: {
    title: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    options: { type: Array as PropType<any[]>, default: () => [] },
    modelValue: { type: Array as PropType<FormFieldOptionCreationData[]>, default: () => [] },
    errorMessages: { type: String, default: '' },
  },
  emits: ['close', 'update:modelValue'],
  setup(props, ctx) {
    const fieldValue = useVModel(props, 'modelValue', ctx.emit)
    const showPureCheckbox = computed(() => props.options.length <= 3)
    const isOpen = ref(false)
    const selectedItems = ref<FormFieldOptionCreationData[]>([])

    const textValueLabel = computed(() => {
      const targetOptions = props.options.filter(opt => fieldValue.value?.some(field => field.sub_id === opt.id)).map(opt => opt.label)
      return targetOptions.length < 3 ? targetOptions.join(', ') : targetOptions[0]
    })

    const fieldValueIds = computed(() => fieldValue.value.map(field => field.sub_id))

    function open() {
      isOpen.value = true
      selectedItems.value = fieldValue.value
    }

    function confirm() {
      fieldValue.value = selectedItems.value
    }

    function close() {
      ctx.emit('close')
    }

    function checkInputChecked(selected: FormFieldOptionCreationData, option: FormFieldOptionCreationData) {
      return selected?.sub_id === option?.sub_id
    }

    return {
      fieldValue,
      showPureCheckbox,
      isOpen,
      selectedItems,
      textValueLabel,
      open,
      confirm,
      close,
      checkInputChecked,
      fieldValueIds,
    }
  },
})
</script>
<style scoped>
:deep(.v-select__menu-icon) {
  display: none;
}
</style>
